/**
 * Global state management with Zustand
 */
import { create } from 'zustand';
import axios from 'axios';

// Configure axios
axios.defaults.baseURL = '/';

const useStore = create((set, get) => ({
  // ── Auth ─────────────────────────────────────────────────────
  user: null,
  token: localStorage.getItem('rockettalk_token') || null,
  isAuthenticated: !!localStorage.getItem('rockettalk_token'),

  setToken: (token) => {
    localStorage.setItem('rockettalk_token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    set({ token, isAuthenticated: true });
  },

  setUser: (user) => set({ user }),

  logout: () => {
    localStorage.removeItem('rockettalk_token');
    delete axios.defaults.headers.common['Authorization'];
    set({
      user: null,
      token: null,
      isAuthenticated: false,
      currentChannel: null,
      socketStatus: 'disconnected',
      assignedChannels: [],
      activeChannel: null,
      mutedChannels: new Set(),
    });
  },

  // ── Socket Status ─────────────────────────────────────────────
  socketStatus: 'disconnected',
  setSocketStatus: (status) => set({ socketStatus: status }),

  // ── Channels ─────────────────────────────────────────────────
  channels: [],
  currentChannel: null,
  channelUsers: {},
  onlineCounts: {},
  channelJoinError: null,

  // ── Assigned Channels / Mute / Active ─────────────────────────
  assignedChannels: [],
  mutedChannels: new Set(),
  activeChannel: null,

  setAssignedChannels: (channels) => set({ assignedChannels: channels }),

  toggleMuteChannel: (channelId) => {
    set(state => {
      const next = new Set(state.mutedChannels);
      if (next.has(channelId)) {
        next.delete(channelId);
      } else {
        next.add(channelId);
      }
      return { mutedChannels: next };
    });
  },

  setActiveChannel: (channel) => set({ activeChannel: channel, currentChannel: channel }),

  setChannels: (channels) => set({ channels }),
  setCurrentChannel: (channel) => set({ currentChannel: channel }),
  setChannelJoinError: (err) => set({ channelJoinError: err }),

  updateChannelCount: (channelId, count) => {
    set(state => ({
      onlineCounts: { ...state.onlineCounts, [channelId]: count }
    }));
  },

  setChannelUsers: (channelId, users) => {
    set(state => ({
      channelUsers: { ...state.channelUsers, [channelId]: users }
    }));
  },

  addChannelUser: (channelId, user) => {
    set(state => {
      const current = state.channelUsers[channelId] || [];
      if (current.find(u => u.userId === user.userId)) return state;
      return { channelUsers: { ...state.channelUsers, [channelId]: [...current, user] } };
    });
  },

  removeChannelUser: (channelId, userId) => {
    set(state => ({
      channelUsers: {
        ...state.channelUsers,
        [channelId]: (state.channelUsers[channelId] || []).filter(u => u.userId !== userId),
      }
    }));
  },

  // ── PTT State ─────────────────────────────────────────────────
  isTransmitting: false,
  transmittingUser: null,

  setTransmitting: (isTransmitting, user = null) => set({ isTransmitting, transmittingUser: user }),

  // Map<channelId, Set<userId>> — used to render live waveforms in ChannelList.
  // Stored as plain object of arrays for shallow-render friendliness.
  activeTransmittingByChannel: {},

  addTransmittingUser: (channelId, userId) => {
    if (!channelId || !userId) return;
    set(state => {
      const cur = new Set(state.activeTransmittingByChannel[channelId] || []);
      cur.add(userId);
      return {
        activeTransmittingByChannel: {
          ...state.activeTransmittingByChannel,
          [channelId]: Array.from(cur),
        },
      };
    });
  },

  removeTransmittingUser: (channelId, userId) => {
    if (!channelId) return;
    set(state => {
      const cur = (state.activeTransmittingByChannel[channelId] || []).filter(u => u !== userId);
      const next = { ...state.activeTransmittingByChannel };
      if (cur.length === 0) delete next[channelId];
      else next[channelId] = cur;
      return { activeTransmittingByChannel: next };
    });
  },

  // ── Online Users ──────────────────────────────────────────────
  onlineUsers: {},

  setUserOnline: (userId, info) => {
    set(state => ({ onlineUsers: { ...state.onlineUsers, [userId]: info } }));
  },
  setUserOffline: (userId) => {
    set(state => {
      const ou = { ...state.onlineUsers };
      delete ou[userId];
      return { onlineUsers: ou };
    });
  },

  // ── Messages ──────────────────────────────────────────────────
  messages: {},
  addMessage: (channelId, msg) => {
    set(state => ({
      messages: {
        ...state.messages,
        [channelId]: [...(state.messages[channelId] || []).slice(-199), msg],
      }
    }));
  },
  setMessages: (channelId, msgs) => {
    set(state => ({ messages: { ...state.messages, [channelId]: msgs } }));
  },
  updateMessage: (channelId, msgId, patch) => {
    set(state => ({
      messages: {
        ...state.messages,
        [channelId]: (state.messages[channelId] || []).map(m =>
          m.id === msgId ? { ...m, ...patch } : m
        ),
      }
    }));
  },

  // ── Toast Notifications ───────────────────────────────────────
  toasts: [],
  addToast: (message, type = 'info') => {
    const id = Date.now() + Math.random();
    set(state => ({ toasts: [...state.toasts, { id, message, type }] }));
    setTimeout(() => {
      set(state => ({ toasts: state.toasts.filter(t => t.id !== id) }));
    }, 3500);
  },
  removeToast: (id) => {
    set(state => ({ toasts: state.toasts.filter(t => t.id !== id) }));
  },
}));

// Set auth header on load
const token = localStorage.getItem('rockettalk_token');
if (token) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

export default useStore;
